import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';

import { type AudienceActivationType, AudienceJobStatus, type AudienceType } from '@/api/audience';
import AudienceActivationDetailDialog from '@/app/audiences/AudienceActivationDetailDialog';
import AudienceDeleteConfirmDialog from '@/app/audiences/AudienceDeleteConfirmDialog';
import Edit from '@/assets/svg/edit.svg?react';
import Delete from '@/assets/svg/trashcan.svg?react';
import InfoPopover from '@/components/InfoPopover';
import {
  InstantSearchDataTable,
  type InstantSearchDataTableColumnDef,
} from '@/components/InstantSearch';
import LetteredAvatar from '@/components/LetteredAvatar';
import { MoreActionsCell } from '@/components/MoreActionsCell';
import { Link } from '@/components/typography';
import { USDateViewFormat } from '@/constants/formats';
import type SolAPIError from '@/error/SolAPIError';
import { numberFormat } from '@/helper/numberFormatter';
import { useMe } from '@/providers/User';
import { ROUTES } from '@/router';
import { COLOR_PRIMARY } from '@/styles/palette';

import AudienceActivationCell from './AudienceActivationCell';
import AudienceActivationWizard from './AudienceActivationWizard';

type Props = {
  data?: AudienceType[];
  error: SolAPIError | null;
  totalResults?: number;
  isLoading?: boolean;
  onAudienceDelete: (deleteId: string) => void;
  onAudienceActivate: (audienceId: string, activation: AudienceActivationType) => void;
  onAudienceDeactivate: (audienceId: string, activation: AudienceActivationType) => void;
};

enum ActionKey {
  EDIT = 'EDIT',
  DELETE = 'DELETE',
}

type AudienceToSeeActivationDetail = {
  audience: AudienceType;
  activation: AudienceActivationType;
};

const AudiencesListTable = ({
  data,
  error,
  totalResults,
  isLoading = false,
  onAudienceDelete,
  onAudienceActivate,
  onAudienceDeactivate,
}: Props) => {
  const [audienceToActivate, setAudienceToActivate] = useState<AudienceType>();
  const [audienceToSeeActivationDetail, setAudienceToSeeActivationDetail] = useState<
    AudienceToSeeActivationDetail | undefined
  >();
  const user = useMe();
  const [audienceToDelete, setAudienceToDelete] = useState<AudienceType>();

  const sortFields = ROUTES.audiences.searchParams['a.sortBy'].enumValues;

  const handleAudienceDeleteCancel = () => {
    setAudienceToDelete(undefined);
  };

  const handleAudienceDelete = async () => {
    if (audienceToDelete) {
      onAudienceDelete(audienceToDelete.id);
      setAudienceToDelete(undefined);
    }
  };

  const handleActionSelect = (actionKey: ActionKey, field: AudienceType) => {
    if (actionKey === ActionKey.DELETE) {
      setAudienceToDelete(field);
    }
  };

  const handleAudienceActivationComplete = async (
    audienceId: string,
    data: { activation: AudienceActivationType },
  ) => {
    await onAudienceActivate(audienceId, data.activation);
    setAudienceToActivate(undefined);
  };

  const handleAudienceActivationDeactivate = async (data: AudienceToSeeActivationDetail) => {
    await onAudienceDeactivate(data.audience.id, data.activation);
    setAudienceToSeeActivationDetail(undefined);
  };

  const columns = useMemo<InstantSearchDataTableColumnDef<AudienceType>[]>(
    () => [
      {
        header: t`Audience Name`,
        id: 'name',
        accessorKey: 'name',
        enableSorting: sortFields.includes('name'),
        minSize: 200,
        flex: 2,
        cell: ({ row }) => {
          const field = row.original;
          const text = field.name;
          return (
            <div className="flex items-center gap-2">
              {field.lastJob?.status === AudienceJobStatus.COMPLETED ? (
                <Link variant="caption1" to={ROUTES.audienceById(field.id)}>
                  {text}
                </Link>
              ) : (
                text
              )}
              {field.isTAM && (
                <LetteredAvatar
                  className="-my-0.5"
                  name="T"
                  color={COLOR_PRIMARY}
                  shape="circle"
                  size="small"
                />
              )}
            </div>
          );
        },
      },
      {
        header: t`Companies`,
        id: 'metrics.size',
        accessorKey: 'metrics.size',
        enableSorting: sortFields.includes('metrics.size'),
        size: 130,
        meta: { align: 'end' },
        cell: ({ row }) => numberFormat(row.original.metrics.size),
      },
      {
        header: t`Visits (Last 30d)`,
        id: 'metrics.visits',
        accessorKey: 'metrics.visits',
        enableSorting: sortFields.includes('metrics.visits'),
        size: 150,
        meta: { align: 'end' },
        cell: ({ row }) => numberFormat(row.original.metrics.visits),
      },
      {
        header: t`Created On`,
        id: 'created_at',
        accessorKey: 'created_at',
        enableSorting: sortFields.includes('created_at'),
        size: 120,
        meta: { align: 'end' },
        cell: ({ row }) => dayjs(row.original.created_at).format(USDateViewFormat),
      },
      {
        header: t`Created By`,
        id: 'created_by.name',
        accessorKey: 'created_by.name',
        enableSorting: sortFields.includes('created_by.name'),
        minSize: 160,
        flex: 1,
        cell: ({ row }) => row.original.created_by?.name ?? t`Unknown User`,
      },
      {
        header: t`Source`,
        id: 'type',
        accessorKey: 'type',
        enableSorting: sortFields.includes('type'),
        size: 120,
        cell: ({ row }) => {
          const text = row.original.type as string;
          switch (text) {
            case 'upload':
              return t`CSV/File`;
            case 'rule':
              return t`Rule-Based`;
            case 'lookalike':
              return t`AI-Based`;
            case 'salesforce':
            case 'hubspot':
            case 'crm':
              return t`CRM`;
            default:
              return t`Unknown`;
          }
        },
      },
      {
        header: t`Status`,
        id: 'lastJob.status',
        size: 100,
        enableSorting: false,
        cell: ({ row }) => {
          const field = row.original;
          const { status } = field.lastJob || {};

          let label = t`Unknown`;
          switch (status) {
            case AudienceJobStatus.COMPLETED:
              label = t`Complete`;
              break;
            case AudienceJobStatus.ERROR:
              label = t`Error`;
              break;
            case AudienceJobStatus.PROCESSING:
              label = t`Processing`;
              break;
            case AudienceJobStatus.QUEUED:
              label = t`Queued`;
              break;
            default:
          }

          return (
            <div className="flex items-center gap-1">
              {label}
              {field.lastJob?.errors && (
                <InfoPopover trigger="hover" title={t`Error`} body={field.lastJob?.errors} />
              )}
            </div>
          );
        },
      },
      {
        header: t`Activated`,
        id: 'activations',
        size: 200,
        enableSorting: false,
        cell: ({ row }) => {
          const field = row.original;
          return field.lastJob?.status === AudienceJobStatus.PROCESSING ? null : (
            <AudienceActivationCell
              audience={field}
              addButtonClassName="opacity-0 transition-opacity duration-[0.4s] ease-in-out group-hover:opacity-100"
              onAudienceActivate={() => setAudienceToActivate(field)}
              onAudienceActivationClick={(activation) =>
                setAudienceToSeeActivationDetail({ audience: field, activation })
              }
            />
          );
        },
      },
      {
        header: '',
        id: 'action',
        size: 86,
        meta: { align: 'end' },
        hidden: !user?.isAdmin,
        enableSorting: false,
        cell: ({ row }) => {
          const field = row.original;
          return (
            <MoreActionsCell<ActionKey>
              menuItems={[
                {
                  key: ActionKey.EDIT,
                  label: t`Edit`,
                  icon: <Edit />,
                  to: ROUTES.audienceEditById(field.id),
                },
                {
                  key: ActionKey.DELETE,
                  label: t`Delete`,
                  danger: true,
                  icon: <Delete />,
                },
              ]}
              onSelect={(key) => handleActionSelect(key, field)}
            />
          );
        },
      },
    ],
    [user?.isAdmin, sortFields],
  );

  return (
    <>
      <div data-testid="audiences-table">
        <InstantSearchDataTable<AudienceType>
          columns={columns}
          data={data}
          totalResults={totalResults}
          loading={isLoading}
          emptyMessage={<Trans>No audiences found.</Trans>}
          error={error}
          getRowId={(record) => record.id}
          initialColumnPinning={{
            start: ['name'],
            end: ['action'],
          }}
        />
      </div>
      <AudienceActivationWizard<{ activation: AudienceActivationType }>
        audience={audienceToActivate}
        onClose={() => setAudienceToActivate(undefined)}
        onComplete={
          audienceToActivate?.id
            ? (data) => handleAudienceActivationComplete(audienceToActivate.id, data)
            : undefined
        }
      />
      <AudienceActivationDetailDialog
        audience={audienceToSeeActivationDetail?.audience}
        activation={audienceToSeeActivationDetail?.activation}
        onClose={() => setAudienceToSeeActivationDetail(undefined)}
        onDeactivate={
          audienceToSeeActivationDetail
            ? () => handleAudienceActivationDeactivate(audienceToSeeActivationDetail)
            : undefined
        }
      />
      <AudienceDeleteConfirmDialog
        audience={audienceToDelete}
        onOk={handleAudienceDelete}
        onCancel={handleAudienceDeleteCancel}
      />
    </>
  );
};

export default AudiencesListTable;
